import * as React from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import { TabbedMultipleSelectFilter, getIcon } from '@quality24/design-system';

import {
  TabbedFilterComponentData,
  FilterUsageOption,
  GraphQLFilter,
} from '../../../../@types';
import { useQueryFilter } from '../../../../hooks/filterHooks';
import { parseDataValue } from '../../../../utils/dataUtils';
import {
  parseFilterData,
  formatFilterData,
  mergePermissionFilter,
} from '../../../../utils/filterUtils';

const MockQuery = gql`
  query MockQueryNotToBePerformed {
    apiVersion {
      versionName
    }
  }
`;

export interface Props {
  className?: string;
  partitionId?: 'default' | string;
  config: TabbedFilterComponentData;
  baseFilter?: GraphQLFilter;
  permissionFilter?: GraphQLFilter;
}

/**
 * <TabbedSelectFilterContainer> page
 */
export const TabbedSelectFilterContainer: React.FunctionComponent<Props> = ({
  className,
  partitionId = 'default',
  baseFilter = {},
  permissionFilter: userPermissionFilter = {},
  config: {
    id,
    className: configClassName,
    label,
    icon,
    innerFilters,
    customFilter,
    permissionFilters,
    query,
    components,
  },
}) => {
  const { filters, upsertFilter, removeFilter } = useQueryFilter(
    components.map((c) => c.id),
    partitionId,
  );

  // Handle inner filters
  const { getGraphQLFilter, getPermissionFilter } = useQueryFilter(
    innerFilters?.map((f) => (typeof f === 'string' ? f : f.id)) || [],
    partitionId,
  );

  // Retrieve and memoize the graphQLFilters
  // This getter applies any usage modifications
  const graphQLFilter = React.useMemo(
    () =>
      getGraphQLFilter(
        innerFilters?.filter(
          (f) => typeof f !== 'string',
        ) as FilterUsageOption[],
      ),
    [getGraphQLFilter, innerFilters],
  );

  // Merge all filters for the query
  const queryFilter = React.useMemo(() => {
    if (
      (baseFilter && Object.keys(baseFilter).length > 0) ||
      (graphQLFilter && Object.keys(graphQLFilter).length > 0) ||
      (permissionFilters && permissionFilters.length > 0) ||
      (customFilter && Object.keys(customFilter).length > 0)
    ) {
      const permissionFilter = getPermissionFilter(
        userPermissionFilter,
        permissionFilters,
      );

      const parsedFilters = mergePermissionFilter(
        {
          ...baseFilter,
          ...graphQLFilter,
          ...customFilter,
        },
        permissionFilter,
      );

      return Object.keys(parsedFilters).length ? parsedFilters : undefined;
    }
    return undefined;
  }, [
    baseFilter,
    customFilter,
    getPermissionFilter,
    graphQLFilter,
    permissionFilters,
    userPermissionFilter,
  ]);

  // Prepare the query to eventually fetch filter
  const [fetchData, { data, loading }] = useLazyQuery(
    query ? gql([query]) : MockQuery,
    { variables: { filter: queryFilter } },
  );

  // Parse query filter format
  const values: Record<string, string[]> = React.useMemo(
    () =>
      components.reduce((acc, c) => {
        acc[c.id] = [''];
        const filterValue = filters[c.id];
        if (filterValue) {
          const parsed = parseFilterData(c.parser, filterValue, {
            parserOptions: c.parserOptions,
          });
          if (parsed) {
            acc[c.id] = Array.isArray(parsed) ? parsed : [parsed];
          }
        }
        return acc;
      }, {} as Record<string, string[]>),
    [filters, components],
  );

  // Build the filter options
  const filterOptions = React.useMemo(
    () =>
      components.map((c) => ({
        name: c.id,
        label: c.label,
        type: c.type,
        options: c.options
          .flatMap((opt) => {
            if (!opt.rootKey) return opt;
            if (!data) return null;

            return data[opt.rootKey][opt.nodesKey || 'nodes'].map(
              (n: Record<string, unknown>) => ({
                key: opt.key
                  ? parseDataValue(n, opt.key, opt.dataType || 'string')
                  : undefined,
                id: parseDataValue(n, opt.id, opt.dataType || 'string'),
                label: parseDataValue(n, opt.label, opt.dataType || 'string'),
              }),
            );
          })
          .filter((opt) => opt),
      })),
    [components, data],
  );

  /**
   * Handles the filter change, saving it the the context
   */
  const handleFilterChange = React.useCallback(
    (name: string, newValue: string | string[]) => {
      const comp = components.find((c) => c.id === name);
      if (!comp) return;

      const isEmptyArray =
        Array.isArray(newValue) &&
        (newValue.length === 0 || isEqual(newValue, ['']));
      if (isEmptyArray || newValue === '') {
        removeFilter(name);
        return;
      }

      const valueToFormat = comp.type === 'radio' ? newValue[0] : newValue;
      const formatted = formatFilterData(comp.parser, valueToFormat, {
        parserOptions: comp.parserOptions,
      });
      if (formatted) {
        upsertFilter(name, formatted);
      }
    },
    [upsertFilter, removeFilter, components],
  );

  // Add log for debuging with DVC-Designer
  if (process.env.REACT_APP_HOST_PROJECT === 'designer') {
    // eslint-disable-next-line no-console
    console.info('TabbedSelectFilterContainer', {
      filterContext: filters,
    });
  }

  return (
    <TabbedMultipleSelectFilter
      className={classNames(className, configClassName)}
      name={id}
      label={label}
      placeholder="Filtros"
      icon={icon ? getIcon(icon) : undefined}
      tabs={filterOptions}
      value={values}
      loading={loading}
      onOpen={() => {
        if (query) fetchData();
      }}
      onChange={handleFilterChange}
    />
  );
};

export default TabbedSelectFilterContainer;
